/**
 * krida-ui-tutorial-overlay.js - Tutorial Overlay UI Module
 *
 * Renders tooltip/callout overlays anchored to game elements during the
 * guided first-game tutorial. Manages highlight effects on target elements.
 *
 * This is NOT the existing krida-tutorial.js video walkthrough system.
 * This module provides in-game overlays during live play.
 */

let activeOverlay = null;
let activeHighlight = null;
let activeHighlights = [];  // For multi-element highlights
let activeBackdrop = null;

/**
 * Show a guided tutorial overlay with text and optional target highlighting.
 * Used by the tutorial engine for interactive scenario guide steps.
 * @param {string} text - Instruction or narration text to display
 * @param {HTMLElement|HTMLElement[]|null} targetElement - Element(s) to highlight
 * @param {Function} [onDismiss] - Callback when overlay is dismissed
 */
export function showGuideOverlay(text, targetElement, onDismiss, nav) {
    dismiss();

    const overlay = document.createElement('div');
    overlay.className = 'tutorial-overlay-tooltip tutorial-guide-overlay';
    overlay.setAttribute('role', 'tooltip');

    if (onDismiss) {
        // Informational step — dim background only when not anchored to a target
        if (!targetElement) {
            const backdrop = document.createElement('div');
            backdrop.className = 'tutorial-overlay-backdrop';
            document.body.appendChild(backdrop);
            activeBackdrop = backdrop;
        }

        overlay.innerHTML = `
            <div class="tutorial-overlay-text">${text}</div>
            ${_buildButtonBarHTML(nav)}
        `;
        _wireButtonBar(overlay, onDismiss, nav);
    } else {
        // Instruction tooltip — no OK, no blocking. Player interacts directly.
        overlay.innerHTML = `
            <div class="tutorial-overlay-text">${text}</div>
        `;
        overlay.style.pointerEvents = 'none';
    }

    document.body.appendChild(overlay);
    activeOverlay = overlay;

    // Handle single element, array of elements, or null
    if (Array.isArray(targetElement)) {
        const validEls = targetElement.filter(el => el);
        validEls.forEach(el => {
            el.classList.add('tutorial-highlight');
            activeHighlights.push(el);
        });
        if (validEls.length > 0) {
            positionOverlayAroundGroup(overlay, validEls);
        } else {
            overlay.classList.add('tutorial-overlay-centered');
        }
    } else if (targetElement) {
        targetElement.classList.add('tutorial-highlight');
        activeHighlight = targetElement;
        positionOverlay(overlay, targetElement, 'up');
    } else {
        overlay.classList.add('tutorial-overlay-centered');
    }
}

/**
 * Show a narration overlay positioned below a target element,
 * without highlighting it or dimming the background.
 * @param {string} text - Narration text
 * @param {HTMLElement} target - Element below which the overlay sits
 * @param {Function} onDismiss - Callback when OK is clicked
 * @param {Object} [nav] - Optional Back/Next/Close callbacks
 */
export function showNarrationBelow(text, target, onDismiss, nav, title) {
    dismiss();

    const overlay = document.createElement('div');
    overlay.className = 'tutorial-overlay-tooltip tutorial-guide-overlay';
    overlay.setAttribute('role', 'tooltip');
    overlay.innerHTML = `
        ${title ? `<div class="tutorial-overlay-title">${title}</div>` : ''}
        <div class="tutorial-overlay-text">${text}</div>
        ${_buildButtonBarHTML(nav)}
    `;
    _wireButtonBar(overlay, onDismiss, nav);

    document.body.appendChild(overlay);
    activeOverlay = overlay;

    if (target) {
        overlay.style.position = 'fixed';
        overlay.style.top = '0px';
        overlay.style.left = '0px';
        requestAnimationFrame(() => {
            const rect = target.getBoundingClientRect();
            const ow = overlay.offsetWidth;
            const oh = overlay.offsetHeight;
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const padding = 8;

            let left = rect.left + (rect.width / 2) - (ow / 2);
            let top = rect.bottom + padding;

            left = Math.max(8, Math.min(left, vw - ow - 8));
            top = Math.max(8, Math.min(top, vh - oh - 8));

            overlay.style.top = `${top}px`;
            overlay.style.left = `${left}px`;
        });
    } else {
        overlay.classList.add('tutorial-overlay-centered');
    }
}

/**
 * Show a narration overlay positioned above a target element,
 * without highlighting it or dimming the background.
 * @param {string} text - Narration text
 * @param {HTMLElement} target - Element above which the overlay sits
 * @param {Function} onDismiss - Callback when OK is clicked
 */
export function showNarrationAbove(text, target, onDismiss, nav, title) {
    dismiss();

    const overlay = document.createElement('div');
    overlay.className = 'tutorial-overlay-tooltip tutorial-guide-overlay';
    overlay.setAttribute('role', 'tooltip');
    overlay.innerHTML = `
        ${title ? `<div class="tutorial-overlay-title">${title}</div>` : ''}
        <div class="tutorial-overlay-text">${text}</div>
        ${_buildButtonBarHTML(nav)}
    `;
    _wireButtonBar(overlay, onDismiss, nav);

    document.body.appendChild(overlay);
    activeOverlay = overlay;

    if (target) {
        // Pre-position so layout settles before measuring
        overlay.style.position = 'fixed';
        overlay.style.top = '0px';
        overlay.style.left = '0px';
        requestAnimationFrame(() => {
            const rect = target.getBoundingClientRect();
            const ow = overlay.offsetWidth;
            const oh = overlay.offsetHeight;
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const padding = 8;

            let left = rect.left + (rect.width / 2) - (ow / 2);
            let top = rect.top - oh - padding;

            left = Math.max(8, Math.min(left, vw - ow - 8));
            top = Math.max(8, Math.min(top, vh - oh - 8));

            overlay.style.top = `${top}px`;
            overlay.style.left = `${left}px`;
        });
    } else {
        overlay.classList.add('tutorial-overlay-centered');
    }
}

/**
 * Show a narration overlay positioned inside a container element,
 * without highlighting the container or dimming the background.
 * @param {string} text - Narration text
 * @param {HTMLElement} container - Element whose bounds the overlay is centered within
 * @param {Function} onDismiss - Callback when OK is clicked
 */
export function showNarrationInside(text, container, onDismiss, nav, title) {
    dismiss();

    const overlay = document.createElement('div');
    overlay.className = 'tutorial-overlay-tooltip tutorial-guide-overlay';
    overlay.setAttribute('role', 'tooltip');
    overlay.innerHTML = `
        ${title ? `<div class="tutorial-overlay-title">${title}</div>` : ''}
        <div class="tutorial-overlay-text">${text}</div>
        ${_buildButtonBarHTML(nav)}
    `;
    _wireButtonBar(overlay, onDismiss, nav);

    document.body.appendChild(overlay);
    activeOverlay = overlay;

    if (container) {
        overlay.style.position = 'fixed';
        overlay.style.top = '0px';
        overlay.style.left = '0px';
        requestAnimationFrame(() => {
            const rect = container.getBoundingClientRect();
            const ow = overlay.offsetWidth;
            const oh = overlay.offsetHeight;
            const vw = window.innerWidth;
            const vh = window.innerHeight;

            let left = rect.left + (rect.width / 2) - (ow / 2);
            let top = rect.top + (rect.height / 2) - (oh / 2);

            left = Math.max(8, Math.min(left, vw - ow - 8));
            top = Math.max(8, Math.min(top, vh - oh - 8));

            overlay.style.top = `${top}px`;
            overlay.style.left = `${left}px`;
        });
    } else {
        overlay.classList.add('tutorial-overlay-centered');
    }
}

/**
 * Remove the current overlay and highlight.
 */
export function dismiss() {
    if (activeBackdrop) {
        activeBackdrop.remove();
        activeBackdrop = null;
    }
    if (activeOverlay) {
        activeOverlay.remove();
        activeOverlay = null;
    }
    if (activeHighlight) {
        activeHighlight.classList.remove('tutorial-highlight');
        activeHighlight = null;
    }
    // Clear multi-element highlights
    activeHighlights.forEach(el => el.classList.remove('tutorial-highlight'));
    activeHighlights = [];
}

/**
 * Check if an overlay is currently displayed.
 * @returns {boolean}
 */
export function isActive() {
    return activeOverlay !== null;
}

// Build the button bar HTML — uses Prev/Next/Close if `nav` is provided,
// otherwise falls back to a single OK button.
function _buildButtonBarHTML(nav) {
    if (!nav) {
        return `<button class="action-btn tutorial-overlay-dismiss" aria-label="Dismiss">OK</button>`;
    }
    const prevDisabled = nav.canPrev === false ? 'disabled' : '';
    return `
        <div class="tutorial-overlay-nav">
            <button class="action-btn tutorial-overlay-nav-prev" ${prevDisabled}>Back</button>
            <button class="action-btn tutorial-overlay-nav-close">Close</button>
            <button class="action-btn tutorial-overlay-nav-next">Next</button>
        </div>
    `;
}

function _wireButtonBar(overlay, onDismiss, nav) {
    if (!nav) {
        const dismissBtn = overlay.querySelector('.tutorial-overlay-dismiss');
        if (dismissBtn) {
            dismissBtn.addEventListener('click', () => {
                dismiss();
                if (onDismiss) onDismiss();
            });
        }
        return;
    }
    const prevBtn = overlay.querySelector('.tutorial-overlay-nav-prev');
    const nextBtn = overlay.querySelector('.tutorial-overlay-nav-next');
    const closeBtn = overlay.querySelector('.tutorial-overlay-nav-close');
    if (prevBtn && nav.onPrev) {
        prevBtn.addEventListener('click', () => {
            dismiss();
            nav.onPrev();
        });
    }
    if (nextBtn) {
        nextBtn.addEventListener('click', () => {
            dismiss();
            // Prefer onNext callback (advances scenario or wizard step) over onDismiss
            if (nav.onNext) nav.onNext();
            else if (onDismiss) onDismiss();
        });
    }
    if (closeBtn && nav.onClose) {
        closeBtn.addEventListener('click', () => {
            dismiss();
            nav.onClose();
        });
    }
}

function positionOverlayAroundGroup(overlay, elements) {
    requestAnimationFrame(() => {
        // Compute bounding box around all target elements
        let groupTop = Infinity, groupBottom = -Infinity;
        let groupLeft = Infinity, groupRight = -Infinity;
        elements.forEach(el => {
            const r = el.getBoundingClientRect();
            groupTop = Math.min(groupTop, r.top);
            groupBottom = Math.max(groupBottom, r.bottom);
            groupLeft = Math.min(groupLeft, r.left);
            groupRight = Math.max(groupRight, r.right);
        });

        const overlayRect = overlay.getBoundingClientRect();
        const padding = 12;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const groupCenterX = (groupLeft + groupRight) / 2;

        let top, left;

        // Prefer placing below the group; if not enough room, place above
        if (groupBottom + padding + overlayRect.height <= vh - 8) {
            top = groupBottom + padding;
        } else {
            top = groupTop - overlayRect.height - padding;
        }

        left = groupCenterX - (overlayRect.width / 2);

        // Clamp to viewport
        left = Math.max(8, Math.min(left, vw - overlayRect.width - 8));
        top = Math.max(8, Math.min(top, vh - overlayRect.height - 8));

        overlay.style.position = 'fixed';
        overlay.style.top = `${top}px`;
        overlay.style.left = `${left}px`;
    });
}

function positionOverlay(overlay, target, arrowDir) {
    const rect = target.getBoundingClientRect();
    const padding = 12;

    // Let the CSS handle initial sizing, then position
    requestAnimationFrame(() => {
        const overlayRect = overlay.getBoundingClientRect();

        let top, left;

        if (arrowDir === 'down') {
            // Overlay above the target
            top = rect.top - overlayRect.height - padding;
            left = rect.left + (rect.width / 2) - (overlayRect.width / 2);
        } else if (arrowDir === 'up') {
            // Overlay below the target
            top = rect.bottom + padding;
            left = rect.left + (rect.width / 2) - (overlayRect.width / 2);
        } else {
            // Default: below
            top = rect.bottom + padding;
            left = rect.left + (rect.width / 2) - (overlayRect.width / 2);
        }

        // Clamp to viewport
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        left = Math.max(8, Math.min(left, vw - overlayRect.width - 8));
        top = Math.max(8, Math.min(top, vh - overlayRect.height - 8));

        overlay.style.position = 'fixed';
        overlay.style.top = `${top}px`;
        overlay.style.left = `${left}px`;
    });
}
